import React, { useState, useEffect } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonMenuButton,
  IonLoading,
} from "@ionic/react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import {
  TrendingDown,
  TrendingUp,
  AttachMoney,
  CalendarToday,
  Download,
  Search,
  CopyAll,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
// import { useLedger } from "../../hooks/useLedger";
import { formatCurrencyN } from "../../../../common/utils_t";
import { callToast } from "../../../../common/utils";
import { useToast } from "../../../../context/ToastContext";
import { useLedger } from "../../../../hooks/useLedger";
// import { useToast } from "../../contexts/ToastContext";

const AllReport: React.FC = () => {
  const { transactions, summary, loading, getAllPostingReport } = useLedger();
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());
  const [reportGenerated, setReportGenerated] = useState(false);

  const toast = useToast();

  const handleGenerateReport = async () => {
    const success = await getAllPostingReport({
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
    });

    if (success) {
      setReportGenerated(true);
    }
  };

  const handleExport = () => {
    if (!transactions.length) return;

    // Create CSV content
    const headers = [
      "#",
      "Date",
      "Description",
      "Debit (Dr)",
      "Credit (Cr)",
      "Balance (Cr - Dr)",
    ];
    const rows = transactions.map((transaction, index) => {
      const debit = parseFloat(String(transaction.dr)) || 0;
      const credit = parseFloat(String(transaction.cr)) || 0;
      let runningBalance = 0;

      // Calculate running balance up to this transaction
      for (let i = 0; i <= index; i++) {
        const cr = parseFloat(String(transactions[i].cr)) || 0;
        const dr = parseFloat(String(transactions[i].dr)) || 0;
        runningBalance += cr - dr;
      }

      return [
        index + 1,
        dayjs(transaction.dateCreated).format("DD MMM YYYY HH:mm"),
        transaction.description || "",
        debit > 0 ? formatCurrencyN(debit).replace("₦", "") : "",
        credit > 0 ? formatCurrencyN(credit).replace("₦", "") : "",
        formatCurrencyN(runningBalance).replace("₦", ""),
      ];
    });

    // Add summary row
    if (summary) {
      rows.push([]);
      rows.push(["SUMMARY", "", "", "", "", ""]);
      rows.push([
        "Total Debit",
        "",
        "",
        formatCurrencyN(summary.totalDebit).replace("₦", ""),
        "",
        "",
      ]);
      rows.push([
        "Total Credit",
        "",
        "",
        "",
        formatCurrencyN(summary.totalCredit).replace("₦", ""),
        "",
      ]);
      rows.push([
        "Net Balance",
        "",
        "",
        "",
        "",
        formatCurrencyN(summary.netBalance).replace("₦", ""),
      ]);
    }

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `all-ledger-report-${dayjs().format("YYYY-MM-DD")}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const calculateRunningBalance = (index: number): number => {
    let balance = 0;
    for (let i = 0; i <= index; i++) {
      const credit = parseFloat(String(transactions[i].cr)) || 0;
      const debit = parseFloat(String(transactions[i].dr)) || 0;
      balance += credit - debit;
    }
    return balance;
  };

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>All Report</IonTitle>
          <IonButtons slot="end">
            {reportGenerated && transactions.length > 0 && (
              <IonButton onClick={handleExport}>
                <Download />
                EXPORT
              </IonButton>
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {/* <LocalizationProvider dateAdapter={AdapterDayjs}> */}
          <Box sx={{ p: 2 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
                All Postings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View detailed posting history and financial reports for all
                postings
              </Typography>
            </Box>

            {/* Report Filters */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Report Filters
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(newValue) => newValue && setStartDate(newValue)}
                    sx={{ fontSize: "0.75rem" }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(newValue) => newValue && setEndDate(newValue)}
                    sx={{ fontSize: "0.75rem" }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Search />}
                    onClick={handleGenerateReport}
                    disabled={loading}
                    sx={{ height: "56px", fontSize: "0.75rem" }}
                  >
                    GENERATE REPORT
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Summary Cards - Only show after report is generated */}
            {reportGenerated && summary && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: "#ffebee" }}>
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <TrendingDown sx={{ color: "#d32f2f", mr: 1 }} />
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem", color: "#d32f2f" }}
                        >
                          Total Debit (Dr)
                        </Typography>
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", color: "#d32f2f" }}
                      >
                        {formatCurrencyN(summary.totalDebit)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: "#e8f5e9" }}>
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <TrendingUp sx={{ color: "#2e7d32", mr: 1 }} />
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem", color: "#2e7d32" }}
                        >
                          Total Credit (Cr)
                        </Typography>
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", color: "#2e7d32" }}
                      >
                        {formatCurrencyN(summary.totalCredit)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: "#e3f2fd" }}>
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <AttachMoney sx={{ color: "#1976d2", mr: 1 }} />
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem", color: "#1976d2" }}
                        >
                          Net Balance (Cr - Dr)
                        </Typography>
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", color: "#1976d2" }}
                      >
                        {formatCurrencyN(summary.netBalance)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: "#e0f2f1" }}>
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <CalendarToday sx={{ color: "#00695c", mr: 1 }} />
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem", color: "#00695c" }}
                        >
                          Transactions
                        </Typography>
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", color: "#00695c" }}
                      >
                        {summary.transactionCount}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* Transaction History Table - Only show after report is generated */}
            {reportGenerated && transactions.length > 0 && (
              <Paper sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>
                      Transaction History
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Showing {transactions.length} transactions from{" "}
                      {startDate.format("DD MMM YYYY")} to{" "}
                      {endDate.format("DD MMM YYYY")}
                    </Typography>
                  </Box>
                  <Chip label="Active" color="success" size="small" />
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Post Code</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Debit (Dr)</TableCell>
                        <TableCell align="right">Credit (Cr)</TableCell>
                        <TableCell align="right">
                          Balance <br /> (Cr - Dr)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((transaction, index) => {
                        const debit = parseFloat(String(transaction.dr)) || 0;
                        const credit = parseFloat(String(transaction.cr)) || 0;
                        const balance = calculateRunningBalance(index);
                        return (
                          <TableRow key={index}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>
                              {dayjs(transaction.dateCreated).format(
                                "DD MMM YYYY HH:mm"
                              )}
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {transaction.postCode || "-"}
                                <CopyAll
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      transaction.postCode
                                    );
                                    toast.showSuccess(
                                      "Post Code copied to clipboard"
                                    );
                                  }}
                                />
                              </Box>
                            </TableCell>
                            <TableCell>{transaction.desp || "-"}</TableCell>
                            <TableCell align="right">
                              {debit > 0 ? formatCurrencyN(debit) : "-"}
                            </TableCell>
                            <TableCell align="right">
                              {credit > 0 ? formatCurrencyN(credit) : "-"}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrencyN(balance)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Empty State */}
            {reportGenerated && transactions.length === 0 && (
              <Paper sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="h6" color="text.secondary">
                  No transactions found for the selected period
                </Typography>
              </Paper>
            )}
          </Box>
        {/* </LocalizationProvider> */}
      </IonContent>
      <IonLoading isOpen={loading} message="Generating report..." />
    </IonPage>
  );
};

export default AllReport;
